import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Eye, Heart, Play, LayoutGrid, Flame, BarChart3, GraduationCap, ShieldCheck, UploadCloud, Users, ChevronDown, Bot, HeartPulse, Palette, Navigation, Building2, Sparkles, Star } from "lucide-react";
import api, { resolveImage } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useLiked } from "../lib/likes";
import { useAuth } from "../context/AuthContext";

const CAT_ICONS = {
  "AI & Robotics": Bot,
  "Bio & Health": HeartPulse,
  "Digital Arts & Design": Palette,
  "Smart Mobility": Navigation,
  "Future Architecture": Building2,
};
const catIcon = (c) => CAT_ICONS[c] || Sparkles;

const hashOf = (s = "") => [...s].reduce((a, c) => a + c.charCodeAt(0), 0);
const ratingFor = (p) => (4.2 + (hashOf(p.id) % 8) / 10).toFixed(1);

const TAG_STYLES = [
  "border-blue-200 bg-blue-50 text-blue-700",
  "border-emerald-200 bg-emerald-50 text-emerald-700",
  "border-violet-200 bg-violet-50 text-violet-700",
];

const isNew = (p) => p.created_at && Date.now() - new Date(p.created_at).getTime() < 14 * 24 * 3600 * 1000;

const MarketCard = ({ p, index, onOpen, t }) => {
  const liked = useLiked(p.id);
  const { user } = useAuth();
  return (
  <motion.article
    layout
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-30px" }}
    transition={{ duration: 0.55, delay: (index % 5) * 0.06, ease: [0.16, 1, 0.3, 1] }}
    data-testid={`market-card-${p.id}`}
    className="group flex flex-col overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-[0_10px_32px_-6px_rgba(15,23,42,0.16)] transition-all duration-400 hover:-translate-y-1 hover:border-[#2563EB]/50 hover:shadow-[0_24px_56px_-10px_rgba(30,58,138,0.3)]"
  >
    <div className="relative h-28 overflow-hidden">
      <img src={resolveImage(p.image_url)} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-600 group-hover:scale-105" />
      {isNew(p) && (
        <span className="absolute left-2.5 top-2.5 rounded bg-[#16A34A] px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-white shadow" data-testid={`market-new-badge-${p.id}`}>
          NEW
        </span>
      )}
    </div>
    <div className="flex flex-1 flex-col p-3.5">
      <h3 className="font-display text-sm font-semibold leading-snug text-slate-900 line-clamp-1">{p.title}</h3>
      <p className="mt-1 text-[11px] leading-relaxed text-slate-500 line-clamp-2">{p.tagline || p.description}</p>
      <div className="mt-2.5 flex flex-wrap gap-1">
        {(p.tech_stack || []).slice(0, 3).map((tech, ti) => (
          <span key={tech} className={`rounded border px-1.5 py-0.5 font-mono text-[8px] ${TAG_STYLES[ti % 3]}`}>{tech}</span>
        ))}
      </div>
      <p className="mt-2.5 truncate font-mono text-[9px] text-slate-400">
        {t.mkt_by} {(p.team_members || [])[0] || "—"}{(p.team_members || []).length > 1 ? ` +${p.team_members.length - 1}` : ""}
      </p>
      <div className="mt-2 flex items-center gap-3 font-mono text-[9px] uppercase tracking-[0.1em] text-slate-400">
        <span className="flex items-center gap-1" data-testid={`market-views-${p.id}`}><Users size={11} className="text-[#2563EB]" /> {p.views.toLocaleString()}</span>
        <span className="flex items-center gap-1"><Star size={11} className="text-amber-400" fill="currentColor" /> {ratingFor(p)}</span>
        <span className="flex items-center gap-1"><Heart size={11} className={liked ? "text-rose-500" : "text-rose-400"} fill={liked ? "currentColor" : "none"} /> {p.likes.toLocaleString()}</span>
      </div>
      <div className="mt-3 flex gap-1.5 border-t border-slate-100 pt-3">
        {p.demo_url && user && (
          <a
            href={p.demo_url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            data-testid={`market-run-${p.id}`}
            className="flex flex-1 items-center justify-center gap-1 rounded-md bg-[#1E3A8A] px-2 py-2 font-mono text-[9px] uppercase tracking-[0.1em] text-white transition-colors hover:bg-[#2563EB]"
          >
            <Play size={10} /> {t.mkt_run}
          </a>
        )}
        <button
          onClick={() => onOpen(p)}
          data-testid={`market-details-${p.id}`}
          className="flex flex-1 items-center justify-center gap-1 rounded-md border border-slate-200 px-2 py-2 font-mono text-[9px] uppercase tracking-[0.1em] text-slate-600 transition-colors hover:border-[#2563EB]/50 hover:text-[#1E3A8A]"
        >
          {t.mkt_details}
        </button>
      </div>
    </div>
  </motion.article>
  );
};

const ValueStrip = ({ t }) => {
  const items = [
    { icon: GraduationCap, title: t.val1_t, body: t.val1_b },
    { icon: ShieldCheck, title: t.val2_t, body: t.val2_b },
    { icon: UploadCloud, title: t.val3_t, body: t.val3_b },
    { icon: Users, title: t.val4_t, body: t.val4_b },
  ];
  return (
    <div className="mt-14 rounded-2xl bg-[#0A0E29] px-8 py-9 lg:px-12" data-testid="value-strip">
      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((item, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
            className="flex gap-4"
            data-testid={`value-item-${i}`}
          >
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#2563EB]/20">
              <item.icon size={20} className="text-blue-300" />
            </div>
            <div>
              <h4 className="font-display text-sm font-semibold text-white">{item.title}</h4>
              <p className="mt-1 text-xs leading-relaxed text-slate-400">{item.body}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default function MarketplaceSection({ projects, onOpen }) {
  const { t } = useLang();
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("popular");
  const [category, setCategory] = useState("");
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    const fetchSummary = () => api.get("/analytics/summary").then((r) => setSummary(r.data)).catch(() => {});
    fetchSummary();
    window.addEventListener("kmu:data-changed", fetchSummary);
    const interval = setInterval(fetchSummary, 30000);
    return () => {
      window.removeEventListener("kmu:data-changed", fetchSummary);
      clearInterval(interval);
    };
  }, []);

  const categories = useMemo(() => [...new Set(projects.map((p) => p.category))], [projects]);
  const catLabels = useMemo(() => Object.fromEntries(projects.filter((p) => p.category_label).map((p) => [p.category, p.category_label])), [projects]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = projects.filter((p) => {
      if (category && p.category !== category) return false;
      if (q && ![p.title, p.tagline, p.description, (p.tech_stack || []).join(" ")].join(" ").toLowerCase().includes(q)) return false;
      return true;
    });
    list.sort((a, b) => {
      const f = (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
      if (f) return f;
      if (a.featured && b.featured) return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      return sort === "popular" ? b.views - a.views : new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });
    return list;
  }, [projects, search, sort, category]);

  const shown = filtered.slice(0, 10);
  const avgRating = projects.length
    ? (projects.reduce((a, p) => a + parseFloat(ratingFor(p)), 0) / projects.length).toFixed(1)
    : "—";

  return (
    <section id="marketplace" data-testid="marketplace-section" className="section-white border-y border-slate-200/70">
      <div className="mx-auto max-w-[1440px] px-5 py-14 lg:px-10 lg:py-16">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search size={17} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="market-search-input"
              placeholder={t.mkt_search}
              className="w-full rounded-xl border border-slate-200 bg-white py-3.5 pl-12 pr-4 text-sm text-slate-800 placeholder-slate-400 shadow-sm outline-none transition-colors focus:border-[#2563EB]"
            />
          </div>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            data-testid="market-sort-select"
            className="rounded-xl border border-slate-200 bg-white px-5 py-3.5 text-sm text-slate-600 shadow-sm outline-none transition-colors focus:border-[#2563EB]"
          >
            <option value="popular">{t.mkt_sort_popular}</option>
            <option value="latest">{t.mkt_sort_latest}</option>
          </select>
        </div>

        <div className="mt-5 flex flex-wrap gap-2.5" data-testid="market-category-tabs">
          <button
            onClick={() => setCategory("")}
            data-testid="market-tab-all"
            className={`flex items-center gap-2 rounded-lg px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] transition-all duration-300 ${
              category === "" ? "bg-[#1E3A8A] text-white shadow-[0_8px_22px_rgba(30,58,138,0.3)]" : "border border-slate-200 bg-white text-slate-500 hover:border-[#2563EB]/50 hover:text-[#1E3A8A]"
            }`}
          >
            <LayoutGrid size={13} /> {t.mkt_all}
          </button>
          {categories.map((c) => {
            const Icon = catIcon(c);
            return (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`market-tab-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`flex items-center gap-2 rounded-lg px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] transition-all duration-300 ${
                  category === c ? "bg-[#1E3A8A] text-white shadow-[0_8px_22px_rgba(30,58,138,0.3)]" : "border border-slate-200 bg-white text-slate-500 hover:border-[#2563EB]/50 hover:text-[#1E3A8A]"
                }`}
              >
                <Icon size={13} /> {catLabels[c] || c}
              </button>
            );
          })}
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="min-w-0">
            {shown.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center" data-testid="market-empty">
                <p className="font-serif-accent text-xl italic text-slate-500">{t.gallery_empty_a}</p>
                <p className="mt-2 text-sm text-slate-400">{t.gallery_empty_b}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5" data-testid="market-grid">
                {shown.map((p, i) => (
                  <MarketCard key={p.id} p={p} index={i} onOpen={onOpen} t={t} />
                ))}
              </div>
            )}
            {filtered.length > 10 && (
              <div className="mt-10 text-center">
                <Link
                  to="/projects"
                  data-testid="market-more-button"
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-7 py-3 text-sm font-medium text-slate-600 shadow-sm transition-all hover:border-[#2563EB] hover:text-[#1E3A8A]"
                >
                  {t.mkt_more} <ChevronDown size={14} />
                </Link>
              </div>
            )}
          </div>

          <aside className="min-w-0 space-y-5">
            <div className="rounded-xl border border-slate-200/90 bg-white p-5 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.06)]" data-testid="trending-panel">
              <h3 className="flex items-center gap-2 font-display text-sm font-bold text-slate-900">
                <Flame size={15} className="text-orange-500" /> {t.mkt_top}
              </h3>
              <div className="mt-4 space-y-1">
                {(summary?.top_projects || []).map((p, i) => {
                  const Icon = catIcon(p.category);
                  return (
                    <button
                      key={p.id}
                      onClick={() => onOpen(projects.find((x) => x.id === p.id) || p)}
                      data-testid={`trending-item-${i}`}
                      className="group flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-slate-50"
                    >
                      <span className="w-4 font-display text-sm font-bold text-slate-300 transition-colors group-hover:text-[#2563EB]">{i + 1}</span>
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#2563EB]/10">
                        <Icon size={14} className="text-[#2563EB]" />
                      </span>
                      <span className="min-w-0 flex-1 truncate text-xs font-medium text-slate-700">{(projects.find((x) => x.id === p.id) || p).title}</span>
                      <span className="flex shrink-0 items-center gap-1 font-mono text-[10px] text-slate-400">
                        <Eye size={11} /> {p.views.toLocaleString()}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200/90 bg-white p-5 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.06)]" data-testid="market-stats-panel">
              <h3 className="flex items-center gap-2 font-display text-sm font-bold text-slate-900">
                <BarChart3 size={15} className="text-[#2563EB]" /> {t.mkt_stats}
              </h3>
              <div className="mt-4 space-y-3.5">
                {[
                  [t.stat_total, summary?.total_projects],
                  [t.stat_users, summary?.total_users],
                  [t.stat_views, summary?.total_views],
                  [t.stat_likes, summary?.total_likes],
                ].map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between border-b border-slate-100 pb-3">
                    <span className="text-xs text-slate-500">{label}</span>
                    <span className="font-display text-base font-bold tabular-nums text-slate-900">{(value ?? 0).toLocaleString()}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">{t.stat_rating}</span>
                  <span className="flex items-center gap-1 font-display text-base font-bold tabular-nums text-slate-900" data-testid="market-avg-rating">
                    <Star size={13} className="text-amber-400" fill="currentColor" /> {avgRating}
                  </span>
                </div>
              </div>
            </div>
          </aside>
        </div>

        <ValueStrip t={t} />
      </div>
    </section>
  );
}
